import { AtIcon, PhoneIcon } from "@xola/icons";
import { Dot, Modal, Phone, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useState } from "react";
import { Traveler, User } from "types";
import { useDebounce } from "use-debounce";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { InfiniteList } from "../Common/InfiniteList";
import { NoTranslate } from "../Common/NoTranslate";
import { SearchInput } from "../Common/SearchInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { Loader } from "@/components/Loader";

interface SearchCustomerModalProps {
    isOpen: boolean;
    initialSearch: string;
    onSubmit: (organizer: Traveler) => void;
    onClose: () => void;
}

export const SearchCustomerModal = (props: SearchCustomerModalProps) => {
    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

interface ModalContentProps {
    initialSearch: string;
    onSubmit: (organizer: Traveler) => void;
    onClose: () => void;
}

const ModalContent = ({ initialSearch, onSubmit, onClose }: ModalContentProps) => {
    const [search, setSearch] = useState(initialSearch);
    const [debouncedSearch] = useDebounce(search, 500);
    const [selectedCustomer, setSelectedCustomer] = useState<Traveler>();

    const {
        data: users,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteCollection<User>("users", { search: debouncedSearch });

    const handleSelect = (organizer: Traveler) => {
        if (organizer.id === selectedCustomer?.id) {
            handleSubmit();
            return;
        }

        setSelectedCustomer(organizer);
    };

    const handleSubmit = () => {
        if (selectedCustomer) {
            onSubmit(selectedCustomer);
        }
    };

    const handleSearchChange = (searchResult: string) => {
        setSearch(searchResult);
    };

    return (
        <>
            <Modal.Header>Search Customer</Modal.Header>

            <Modal.Body className="min-h-[300px] rounded-md border border-gray-light">
                <SearchInput
                    shouldFocus
                    className="m-2"
                    value={search}
                    placeholder="Search by name, email or phone"
                    onChange={handleSearchChange}
                />
                {isLoading ? (
                    <Loader size="tiny" className="p-2" text="Loading" />
                ) : users.length > 0 ? (
                    <InfiniteList
                        hasMore={hasNextPage ?? false}
                        isLoading={isFetchingNextPage}
                        className={clsx("h-60 overflow-y-auto overflow-x-hidden")}
                        onLoadMore={fetchNextPage}
                    >
                        {users.map((user) => (
                            <UserItem
                                key={user.id}
                                user={user}
                                isSelected={selectedCustomer === user}
                                onSelect={handleSelect}
                            />
                        ))}
                    </InfiniteList>
                ) : (
                    <div className="flex justify-center">
                        <span className="pb-4 pt-4 text-lg font-bold text-black">No results found</span>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <fieldset className="flex justify-end space-x-4">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                    <SubmitButton onClick={handleSubmit}>Select Customer</SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

interface UserItemProps {
    user: User;
    onSelect: (user: User) => void;
    isSelected: boolean;
}

const UserItem = ({ user, onSelect, isSelected }: UserItemProps) => {
    return (
        <div
            data-testid="search-customer-modal-item"
            className={clsx(isSelected && "bg-primary text-white", "cursor-pointer border-b border-gray-light p-2")}
            onClick={() => {
                onSelect(user);
            }}
        >
            <NoTranslate className="text-md">{user.name}</NoTranslate>
            <div className="space-x-3 text-gray-darker">
                {user.email && (
                    <NoTranslate className={clsx("text-base", isSelected && "text-white")}>
                        <AtIcon className="mr-1" />
                        {user.email}
                    </NoTranslate>
                )}
                {user.phone &&
                    (user.email ? (
                        <span>
                            <span className="space-x-4">
                                <Dot className="mb-0.5" size="small" color="secondary" />
                            </span>
                            <NoTranslate className={clsx("text-base", isSelected && "text-white")}>
                                <PhoneIcon className="ml-2 mr-1" />
                                <Phone>{user.phone}</Phone>
                            </NoTranslate>
                        </span>
                    ) : (
                        <NoTranslate className={clsx(isSelected && "text-white")}>
                            <PhoneIcon className="mr-1" />
                            <Phone>{user.phone}</Phone>
                        </NoTranslate>
                    ))}
            </div>
        </div>
    );
};
